import { useEffect, useState } from 'react';
import { useTrackEvent, useCarleadHashParam } from '@wkda/funnel-components';

/**
 * Hook to track when an element becomes visible in viewport
 *
 * @param {React.RefObject | HTMLElement} element - Reference to the element to track. In case of a HTMLElement, make sure that window defined
 * @param {Object} trackingData - Data for the tracking event
 * @param {string} trackingData.category - Category for the tracking event
 * @param {string} [trackingData.action = 'View'] - Action for the tracking event (defaults to "View")
 * @param {string} [trackingData.label = 'Element Visible'] - Label for the tracking event (defaults to "Element Visible")
 * @param {string} [trackingData.leadFieldVisible = ''] - Lead field visible label
 * @param {string} [trackingData.method = ''] - Method for the tracking event
 * @param {string} [projectCategory = 'booking'] - Project where the tracking event is used
 * @param {number} [threshold = 0.4] - Visibility threshold (0-1) to trigger the event
 *
 * @returns {boolean} Whether the element has been viewed
 */
export const useTrackElementView = function (element, _ref) {
  let {
    category,
    action = 'View',
    label = 'Element Visible',
    leadFieldVisible = '',
    method = ''
  } = _ref;
  let projectCategory = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'booking';
  let threshold = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0.4;
  const track = useTrackEvent();
  const hash = useCarleadHashParam();
  const [hasTrackedView, setHasTrackedView] = useState(false);
  useEffect(() => {
    const node = element instanceof HTMLElement ? element : element?.current;

    // Don't set up observer if we've already tracked view
    if (hasTrackedView || !node) return;

    // Set up IntersectionObserver to detect when element is visible
    const observer = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry.isIntersecting && !hasTrackedView) {
        // Track when element is visible for the first time
        track({
          action: 'eventTracking',
          category: projectCategory,
          eventData: {
            category,
            action,
            label,
            ...(leadFieldVisible && {
              leadFieldVisible
            }),
            ...(method && {
              method
            }),
            ...(hash && {
              hash
            })
          }
        });
        setHasTrackedView(true);
        // Can disconnect the observer after tracking once
        observer.disconnect();
      }
    }, {
      threshold
    });

    // Start observing the element
    observer.observe(node);

    // Clean up
    return () => {
      observer.disconnect();
    };
  }, [action, category, element, hasTrackedView, hash, label, leadFieldVisible, method, projectCategory, threshold, track]);
  return hasTrackedView;
};